export const SCHEDULE_CELL_FLASH_CLASS = "schedule-cell--flash";
export const SCHEDULE_CELL_FLASH_MS = 700;

export function isElementVisibleInContainer(
  element: { top: number; bottom: number; left: number; right: number },
  container: { top: number; bottom: number; left: number; right: number },
  topInset = 0,
): boolean {
  const topBound = container.top + topInset;
  return (
    element.bottom > topBound &&
    element.top < container.bottom &&
    element.right > container.left &&
    element.left < container.right
  );
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true
  );
}

function flashCellIfVisible(
  cell: Element | null,
  containerRect: { top: number; bottom: number; left: number; right: number },
  topInset: number,
  flashed: HTMLElement[],
): void {
  if (!(cell instanceof HTMLElement) || flashed.includes(cell)) {
    return;
  }
  if (
    !isElementVisibleInContainer(
      cell.getBoundingClientRect(),
      containerRect,
      topInset,
    )
  ) {
    return;
  }

  cell.classList.remove(SCHEDULE_CELL_FLASH_CLASS);
  // Force a reflow so consecutive flashes on the same cell restart.
  void cell.offsetWidth;
  cell.classList.add(SCHEDULE_CELL_FLASH_CLASS);
  flashed.push(cell);
}

export function flashVisibleScheduleCells(
  container: HTMLElement,
  changes: Array<{ person_id: number; activity_id: number; week_id: number }>,
): () => void {
  if (prefersReducedMotion() || changes.length === 0) {
    return () => {};
  }

  const containerRect = container.getBoundingClientRect();
  const thead = container.querySelector(
    ".p-datatable-thead",
  ) as HTMLElement | null;
  const topInset = thead
    ? Math.max(0, thead.getBoundingClientRect().bottom - containerRect.top)
    : 0;

  const seenHourCells = new Set<string>();
  const seenAvailability = new Set<string>();
  const flashed: HTMLElement[] = [];

  for (const change of changes) {
    const hourKey = `${change.person_id}:${change.activity_id}:${change.week_id}`;
    if (!seenHourCells.has(hourKey)) {
      seenHourCells.add(hourKey);
      flashCellIfVisible(
        container.querySelector(
          `[data-schedule-cell="true"][data-emp-id="${change.person_id}"][data-proj-id="${change.activity_id}"][data-week="${change.week_id}"]`,
        ),
        containerRect,
        topInset,
        flashed,
      );
    }

    const availabilityKey = `${change.person_id}:${change.week_id}`;
    if (!seenAvailability.has(availabilityKey)) {
      seenAvailability.add(availabilityKey);
      flashCellIfVisible(
        container.querySelector(
          `[data-schedule-cell="true"][data-availability="true"][data-emp-id="${change.person_id}"][data-week="${change.week_id}"]`,
        ),
        containerRect,
        topInset,
        flashed,
      );
    }
  }

  const timeoutId = window.setTimeout(() => {
    flashed.forEach((cell) => cell.classList.remove(SCHEDULE_CELL_FLASH_CLASS));
  }, SCHEDULE_CELL_FLASH_MS);

  return () => {
    window.clearTimeout(timeoutId);
    flashed.forEach((cell) => cell.classList.remove(SCHEDULE_CELL_FLASH_CLASS));
  };
}
